import { ObjectImage } from "#src/common/system/Object";
import { IEndpointDevice } from "#src/services/endpoint/devices/types";
import WindowsPcIllustration from "#src/assets/images/v2/windows-pc-illustration.png";
import MacPcIllustration from "#src/assets/images/v2/mac-pc-illustration.png";
import LinuxPcIllustration from "#src/assets/images/v2/linux-pc-illustration.png";
import UnknownPcIllustration from "#src/assets/images/v2/unknown-pc-illustration.png";
import IphoneIllustration from "#src/assets/images/v2/iphone-illustration.png";
import PhoneAndroidIllustration from "#src/assets/images/v2/phone-android-illustration.png";
import IpadIllustration from "#src/assets/images/v2/ipad-illustration.png";
import TabletAndroidIllustration from "#src/assets/images/v2/tablet-android-illustration.png";
import PrinterIllustration from "#src/assets/images/v2/printer-illustration.png";
import { useTranslation } from "react-i18next";
import { generatePath, Link } from "react-router-dom";
import { ButtonV2 } from "#src/commonV2/Button";
import { ArrowUpRight } from "@untitled-ui/icons-react";
import { byteToGigabyte } from "#src/utils/common";
import { DateTime } from "luxon";
import i18next from "i18next";
import { Typography } from "@lockerpm/design";
import { pathname } from "#src/config/pathname";
import { getOsIcon, OsEnum } from "../utils";

const getOsIllustration = (
  mainType: string | null,
  subType: string | null,
  os: string | null
) => {
  if (mainType === "workstation") {
    if (os === OsEnum.WINDOWS) return WindowsPcIllustration;
    if (os === OsEnum.APPLE) return MacPcIllustration;
    if (os === OsEnum.UBUNTU) return LinuxPcIllustration;
    return UnknownPcIllustration;
  }
  if (mainType === "mobile") {
    if (subType === "tablet") {
      if (os === OsEnum.APPLE) return IpadIllustration;
      if (os === OsEnum.ANDROID) return TabletAndroidIllustration;
      return TabletAndroidIllustration;
    }
    if (subType === "smart_phone") {
      if (os === OsEnum.APPLE) return IphoneIllustration;
      if (os === OsEnum.ANDROID) return PhoneAndroidIllustration;
      return PhoneAndroidIllustration;
    }
    return PhoneAndroidIllustration;
  }
  if (mainType === "printer") {
    return PrinterIllustration;
  }
  return UnknownPcIllustration;
};

export const EndpointDeviceTableRowExpandContent = ({
  workspaceId,
  item,
}: {
  workspaceId: string;
  item: IEndpointDevice;
}) => {
  const { t } = useTranslation("endpoint", {
    keyPrefix: "devices.table.expandContent",
  });
  const locale = i18next.language;

  const InfoItem = ({ title, content }: { title: string; content: string }) => {
    return (
      <>
        <span className="text-gray-v2-600">{title}</span>
        <Typography.Text
          ellipsis={{
            tooltip: <span className="text-xs font-medium">{content}</span>,
          }}
        >
          <span className="font-medium text-gray-v2-900">{content}</span>
        </Typography.Text>
      </>
    );
  };

  return (
    <div className="px-12 py-4 flex flex-col items-center gap-2">
      <div className="flex items-start gap-12 w-full">
        <div className="flex flex-col items-center text-center gap-1.5 w-[7.25rem] relative">
          {item.os ? (
            <>
              <ObjectImage
                data={getOsIllustration(
                  item.device_type.main_type,
                  item.device_type.sub_type,
                  item.os.family
                )}
                className="h-[6rem] w-[7.25rem] object-contain"
              />
              {item.os.family && getOsIcon(item.os.family, 1.5) ? (
                <div
                  className="absolute h-10 w-10 top-1/3 left-0 bg-gradient-to-b from-[rgba(243,_252,_255,_0.75)] to-[rgba(179,_200,_217,_0.75)] rounded-full
              border-2 border-gray-v2-50 shadow-xs flex items-center justify-center"
                >
                  {getOsIcon(item.os.family, 1.5)}
                </div>
              ) : null}
              <Typography.Text
                ellipsis={{
                  tooltip: (
                    <span className="text-sm font-medium">{item.name}</span>
                  ),
                }}
              >
                <span className="text-sm font-medium">{item.name}</span>
              </Typography.Text>
            </>
          ) : (
            <div className="w-[7.25rem]"></div>
          )}
        </div>
        <div className="grid grid-cols-2 gap-2 pr-6 border-r last:border-0 border-gray-v2-200 flex-1 text-xs">
          <InfoItem title={t("deviceId")} content={item.id.toString()} />
          <InfoItem
            title={t("agentVersion")}
            content={
              item.system && item.system.cystack_version
                ? item.system.cystack_version
                : "---"
            }
          />
          <InfoItem
            title={t("lastUserLogin")}
            content={
              item.last_login_at
                ? (DateTime.fromMillis(item.last_login_at)
                    .setLocale(locale)
                    .toRelative() ?? "---")
                : "---"
            }
          />
          <InfoItem
            title={t("lastLocation")}
            content={
              item.location && item.location.city_country
                ? item.location.city_country
                : "---"
            }
          />
          <InfoItem
            title={t("passportIps")}
            content={
              item.passport_ips.length > 0
                ? item.passport_ips.join(", ")
                : "---"
            }
          />
        </div>
        <div className="grid grid-cols-2 gap-2 border-r last:border-0 border-gray-v2-200 flex-1 text-xs">
          <InfoItem
            title={t("diskEncryption.title")}
            content={
              item.system && item.system.disk_encryption_enabled !== null
                ? item.system.disk_encryption_enabled
                  ? t("diskEncryption.on")
                  : t("diskEncryption.off")
                : "---"
            }
          />
          <InfoItem
            title={t("diskSpace.title")}
            content={
              item.system && item.system.gigs_disk_space_available
                ? item.system.gigs_disk_space_available +
                  " " +
                  t("diskSpace.gbAvailable")
                : "---"
            }
          />
          <InfoItem
            title={t("memory")}
            content={
              item.system && item.system.memory
                ? byteToGigabyte(item.system.memory).toFixed(1) + " GB"
                : "---"
            }
          />
          <InfoItem
            title={t("macAddress")}
            content={
              item.system && item.system.mac_address
                ? item.system.mac_address
                : "---"
            }
          />
        </div>
        <div className="grid grid-cols-2 gap-2 border-r last:border-0 border-gray-v2-200 flex-1 text-xs">
          <InfoItem
            title={t("manufacturer")}
            content={
              item.system && item.system.hardware_vendor
                ? item.system.hardware_vendor
                : "---"
            }
          />
          <InfoItem
            title={t("hardwareModel")}
            content={
              item.system && item.system.hardware_model
                ? item.system.hardware_model
                : "---"
            }
          />
          <InfoItem
            title={t("serialNumber")}
            content={
              item.system && item.system.hardware_serial
                ? item.system.hardware_serial
                : "---"
            }
          />
          <InfoItem
            title={t("cpuBrand")}
            content={
              item.system && item.system.cpu_brand
                ? item.system.cpu_brand
                : "---"
            }
          />
        </div>
      </div>
      {item.is_managed ? (
        <Link
          to={generatePath(pathname.ENDPOINT_DEVICES_OVERVIEW, {
            workspaceId,
            hostId: item.id.toString(),
          })}
        >
          <ButtonV2 variant="tertiary">
            {t("button.details")}
            <ArrowUpRight className="h-5 w-5 min-w-5" />
          </ButtonV2>
        </Link>
      ) : null}
    </div>
  );
};
